import { useState, useEffect, useCallback } from "react";
import { Users, Copy, LogOut, UserMinus } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/i18n/I18nContext";
import { currencySymbol } from "@/i18n/translations";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function TeamSection() {
  const { t } = useI18n();
  const [team, setTeam] = useState(null);
  const [teamPlans, setTeamPlans] = useState([]);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/team/me");
    setTeam(data);
  }, []);
  const loadPlans = useCallback(async () => {
    try { const { data } = await api.get("/payments/team-plans"); setTeamPlans(data.plans); } catch { /* */ }
  }, []);
  useEffect(() => { load(); loadPlans(); }, [load, loadPlans]);

  const buyTeam = async (lk) => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: lk, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch { toast.error("Error"); setBusy(false); }
  };
  const join = async () => {
    if (!code.trim()) return;
    setBusy(true);
    try {
      await api.post("/team/join", { code });
      toast.success(t("joinedTeam"));
      setCode("");
      window.location.reload();
    } catch (e) { toast.error(e.response?.data?.detail || t("invalidCode")); } finally { setBusy(false); }
  };
  const leave = async () => {
    setBusy(true);
    try { await api.post("/team/leave"); window.location.reload(); }
    catch { toast.error("Error"); } finally { setBusy(false); }
  };
  const removeM = async (id) => {
    try { await api.post("/team/remove", { user_id: id }); await load(); } catch { toast.error("Error"); }
  };
  const copy = () => { navigator.clipboard?.writeText(team.invite_code); toast.success(t("copied")); };

  if (!team) return null;
  const yearly = teamPlans.find((p) => p.lookup_key === "vespera_team_yearly");
  const fmtP = (p) => (p ? `${currencySymbol(p.currency.toUpperCase())}${(p.amount / 100).toFixed(0)}` : "");

  return (
    <div className="rounded-xl border border-white/10 bg-card p-4" data-testid="team-section">
      <div className="flex items-center gap-2 mb-3">
        <Users className="h-5 w-5 text-primary" />
        <p className="font-semibold">{t("teamPlan")}</p>
      </div>

      {team.has_team ? (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">{team.name} · {team.seats_used}/{team.seats} {t("seatsUsed")}</p>
          {team.role === "owner" && team.invite_code && (
            <div className="flex items-center gap-2">
              <div className="flex-1 rounded-lg border border-white/10 bg-secondary/60 px-3 py-2 font-mono tracking-widest text-sm" data-testid="team-invite-code">{team.invite_code}</div>
              <Button variant="outline" data-testid="team-copy-code" onClick={copy} className="h-10 border-white/15"><Copy className="h-4 w-4" /></Button>
            </div>
          )}
          <div className="space-y-1.5">
            {team.members.map((m) => (
              <div key={m.id} className="flex items-center justify-between text-sm">
                <span>{m.name} <span className="text-xs text-muted-foreground">· {m.role === "owner" ? t("ownerLabel") : t("memberLabel")}</span></span>
                {team.role === "owner" && m.role !== "owner" && (
                  <button data-testid={`team-remove-${m.id}`} onClick={() => removeM(m.id)}
                    className="text-destructive text-xs flex items-center gap-1"><UserMinus className="h-3.5 w-3.5" />{t("remove")}</button>
                )}
              </div>
            ))}
          </div>
          {team.role === "member" && (
            <Button variant="outline" data-testid="team-leave-btn" onClick={leave} disabled={busy}
              className="w-full h-10 border-white/15 text-destructive hover:text-destructive"><LogOut className="h-4 w-4 mr-1.5" />{t("leaveTeam")}</Button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">{t("teamPlanDesc")}</p>
          <Button data-testid="get-team-btn" onClick={() => buyTeam("vespera_team_yearly")} disabled={busy} className="w-full h-11 font-semibold">
            {t("getTeam")}{yearly ? ` · ${fmtP(yearly)}${t("perYear")}` : ""}
          </Button>
          <div className="pt-2 border-t border-white/10">
            <p className="text-sm font-medium mb-2">{t("joinTeamTitle")}</p>
            <div className="flex items-center gap-2">
              <Input data-testid="team-code-input" value={code} onChange={(e) => setCode(e.target.value)}
                placeholder={t("enterCode")} className="bg-secondary/70 border-white/10 h-10" />
              <Button data-testid="team-join-btn" onClick={join} disabled={busy} className="h-10 px-4 font-semibold">{t("joinBtn")}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
